import { type FC, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "../components/Button";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import {
	useSetXPublishingEnabled,
	useXAdminCheck,
	useXAdminSpend,
} from "./useXAdminSpend";

const money = (n: number) =>
	n.toLocaleString(undefined, {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

export const PageXSpendAdmin: FC = () => {
	const now = useMemo(() => new Date(), []);
	const [year, setYear] = useState(now.getUTCFullYear());
	const [month, setMonth] = useState(now.getUTCMonth() + 1);

	const { data: adminCheck, isLoading: checkLoading } = useXAdminCheck();
	const { data, isLoading } = useXAdminSpend(
		adminCheck?.isAdmin === true,
		year,
		month,
	);
	const setPublishing = useSetXPublishingEnabled();

	if (checkLoading) {
		return (
			<AppPage title="X API Spend">
				<p>Loading...</p>
			</AppPage>
		);
	}

	if (!adminCheck?.isAdmin) {
		return <Navigate to={LinkGen.Settings.Apps()} replace />;
	}

	const publishingEnabled = data?.publishingEnabled ?? true;

	return (
		<AppPage title="X API Spend">
			<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
				<p className="text-muted" style={{ marginBottom: 0 }}>
					Month-to-date X API cost by company (link posts vs non-link vs reads).
					Alert threshold: {money(data?.thresholdUsd ?? 15)} (notify-only). Hard
					spend cap: {money(data?.capUsd ?? 35)} — over cap, posts stay{" "}
					<strong>queued (Held)</strong> until next month (not failed).
				</p>

				<div
					style={{
						display: "flex",
						gap: 12,
						flexWrap: "wrap",
						alignItems: "center",
					}}
				>
					<label style={{ display: "flex", gap: 8, alignItems: "center" }}>
						<span>Year</span>
						<input
							type="number"
							className="form-control"
							style={{ width: 100 }}
							value={year}
							onChange={(e) => setYear(Number(e.target.value))}
						/>
					</label>
					<label style={{ display: "flex", gap: 8, alignItems: "center" }}>
						<span>Month</span>
						<input
							type="number"
							className="form-control"
							style={{ width: 80 }}
							min={1}
							max={12}
							value={month}
							onChange={(e) => setMonth(Number(e.target.value))}
						/>
					</label>
					<div
						style={{
							marginLeft: "auto",
							display: "flex",
							gap: 12,
							alignItems: "center",
						}}
					>
						<span>
							X publishing:{" "}
							<strong>
								{publishingEnabled ? "enabled" : "disabled (kill switch)"}
							</strong>
						</span>
						<Button
							variant={publishingEnabled ? "danger" : "secondary"}
							disabled={setPublishing.isPending}
							onClick={() => setPublishing.mutate(!publishingEnabled)}
						>
							{publishingEnabled
								? "Disable X publishing"
								: "Enable X publishing"}
						</Button>
					</div>
				</div>

				{isLoading || !data ? (
					<p>Loading spend...</p>
				) : data.rows.length === 0 ? (
					<p className="text-muted">No X API calls recorded for this month.</p>
				) : (
					<div className="table-responsive">
						<table className="table table-sm align-middle">
							<thead>
								<tr>
									<th>Company</th>
									<th className="text-end">Total</th>
									<th className="text-end">Link posts</th>
									<th className="text-end">Non-link posts</th>
									<th className="text-end">Reads</th>
									<th className="text-end">Calls</th>
								</tr>
							</thead>
							<tbody>
								{data.rows.map((row) => (
									<tr
										key={row.companyId}
										className={
											row.totalUsd >= (data.capUsd ?? 35)
												? "table-danger"
												: row.totalUsd >= (data.thresholdUsd ?? 15)
													? "table-warning"
													: undefined
										}
									>
										<td>
											{row.companyName}{" "}
											<span className="text-muted">#{row.companyId}</span>
										</td>
										<td className="text-end">{money(row.totalUsd)}</td>
										<td className="text-end">{money(row.linkPostUsd)}</td>
										<td className="text-end">{money(row.nonLinkPostUsd)}</td>
										<td className="text-end">{money(row.readUsd)}</td>
										<td className="text-end">{row.callCount}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</AppPage>
	);
};
